import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import DataGrid, { DataGridItem } from "components/data-grid";
import { injectIntl } from "react-intl";
import OptionalDate from "components/optional-date";
import Thumbnail from "./thumbnail";
import { IntervalSelect, DEFAULT_INTERVAL } from "components/select";
import { withRouter } from "react-router-dom";
import withStyles from "components/with-styles";
import styles from "./styles/thumbnail-live-viewer.css.js";
import { useQuery } from "components/graphql";
import { SimpleButton } from "components/buttons";
import { useImperativeTracking } from "components/tracking";

const ThumbnailLiveViewer = ({
  query,
  getThumbnailDataFromQueryResults,
  showThumbnail,
  showThumbnailDate,
  noDataMessage,
  intervalsInSeconds,
  intl,
  history,
  classes,
  center,
}) => {
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [refreshInterval, setRefreshInterval] = useState(DEFAULT_INTERVAL);
  const track = useImperativeTracking();

  const { data, loading, error, refetch } = useQuery(query);
  const { thumbnailPath, thumbnailDate } = !loading && !error && data ? getThumbnailDataFromQueryResults(data) : {};
  const refreshThumbnail = useCallback(
    () => {
      refetch();
      setLastRefreshed(new Date());
    },
    [refetch],
  );

  useEffect(
    () => {
      if (refreshInterval) {
        const id = setInterval(refreshThumbnail, refreshInterval);
        return () => clearInterval(id);
      }
    },
    [refreshInterval, refreshThumbnail],
  );

  const handleRefreshIntervalChange = value => {
    if (refreshInterval) {
      track("changes the refresh interval in viewer from " + refreshInterval + "ms to " + value + "ms");
    }
    setRefreshInterval(value);
  };

  const handleManualRefreshThumbnail = value => {
    track("refreshes manually the thumbnail in viewer");
    refreshThumbnail();
  };

  const uniqueThumbnailPath = thumbnailPath
    ? `${thumbnailPath}${thumbnailPath.includes("?") ? "&" : "?"}${Date.now()}`
    : null;

  return (
    <DataGrid>
      {thumbnailPath && (
        <>
          {showThumbnail && (
            <DataGridItem big>
              <Thumbnail
                center
                className={classes.sourceThumbnail}
                fullwidth
                path={uniqueThumbnailPath}
                sizeX={1920}
                sizeY={1080}
              />
            </DataGridItem>
          )}
          <DataGridItem>
            <IntervalSelect
              intervalsInSeconds={intervalsInSeconds}
              label={intl.formatMessage({ id: "app.select.interval.refresh" })}
              value={refreshInterval}
              onChange={handleRefreshIntervalChange}
            />
          </DataGridItem>
          <DataGridItem>
            <SimpleButton
              icon="autorenew"
              label={intl.formatMessage({ id: "common.refresh" })}
              onClick={handleManualRefreshThumbnail}
            />
          </DataGridItem>
          <DataGridItem>
            <OptionalDate
              label={intl.formatMessage({ id: "app.source.viewer.lastRefreshed" })}
              showSeconds
              value={lastRefreshed}
            />
          </DataGridItem>
          {showThumbnailDate && (
            <DataGridItem>
              <OptionalDate
                label={intl.formatMessage({ id: "app.source.viewer.thumbnailDate" })}
                showSeconds
                value={thumbnailDate}
              />
            </DataGridItem>
          )}
        </>
      )}
      {!loading && !thumbnailPath && noDataMessage}
    </DataGrid>
  );
};

ThumbnailLiveViewer.propTypes = {
  /**
   * Query para obtener los datos del thumbnail
   **/
  query: PropTypes.object,
  /**
   * Función que obtiene el path de los resultados de la query
   **/
  getThumbnailDataFromQueryResults: PropTypes.func,
  /**
   * Indica si se muestra o no el thumbnail
   **/
  showThumbnail: PropTypes.bool,
  /**
   * Indica si se muestra o no la fecha del thumbnail
   **/
  showThumbnailDate: PropTypes.bool,
  /**
   * Mensaje si no hay url de thumbnail
   **/
  noDataMessage: PropTypes.string,
  /**
   * Intervalos de refresco disponibles
   **/
  intervalsInSeconds: PropTypes.array,
  /**
   * Shows the thumbnail centered
   **/
  center: PropTypes.bool,
};

export default withRouter(injectIntl(withStyles(styles)(ThumbnailLiveViewer)));
